import { equalTo, get, orderByChild, query } from 'firebase/database';
import { logger } from '../../logging';
import { ref } from '../../utils/firebase';

const accountKeyByDdAccountId = new Map<string, Promise<string | null>>();

const isStandaloneNumericId = (value: string): boolean => /^\d+$/.test(value);

const readFirstKey = (snap: {
  forEach: (callback: (child: { key: string | null }) => boolean | void) => unknown;
}): string | null => {
  let accountKey: string | null = null;
  snap.forEach((child) => {
    if (child.key) {
      accountKey = child.key;
      return true;
    }
  });
  return accountKey;
};

const queryAccountKey = async (
  ddAccountId: string | number
): Promise<string | null> => {
  const accountsQuery = query(
    ref('accounts/dotdigital'),
    orderByChild('ddAccountId'),
    equalTo(ddAccountId)
  );
  const snap = await get(accountsQuery);
  return readFirstKey(snap);
};

const lookupAccountKey = async (ddAccountId: string): Promise<string | null> => {
  const stringMatch = await queryAccountKey(ddAccountId);
  if (stringMatch) {
    return stringMatch;
  }

  if (isStandaloneNumericId(ddAccountId)) {
    return queryAccountKey(Number(ddAccountId));
  }

  return null;
};

export const getDamAccountPath = (firebaseKey: string): string =>
  `/accounts/dotdigital/${firebaseKey}`;

export const clearDotdigitalAccountKeyCache = (): void => {
  accountKeyByDdAccountId.clear();
};

/** Indexed lookup of the DAM RTDB key for a DotDigital account id. */
export const findDotdigitalAccountKey = (
  ddAccountId: string | number | null | undefined
): Promise<string | null> => {
  if (ddAccountId == null) {
    return Promise.resolve(null);
  }

  const normalizedId = String(ddAccountId).trim();
  if (!normalizedId) {
    return Promise.resolve(null);
  }

  const cached = accountKeyByDdAccountId.get(normalizedId);
  if (cached) {
    return cached;
  }

  const pending = lookupAccountKey(normalizedId).catch((error) => {
    accountKeyByDdAccountId.delete(normalizedId);
    logger.error(
      `[findDotdigitalAccountKey] failed to resolve DAM account key: ${error}`
    );
    return null;
  });

  accountKeyByDdAccountId.set(normalizedId, pending);
  return pending;
};
